import * as React from 'react';
import _ from 'lodash';
import { toastr } from 'react-redux-toastr';
import ReactGA from 'react-ga';
import { Form } from '../../../../share/components/Form/Form';
import { AgencyForm } from './components/AgencyForm';
import { AgencySubmitView } from './components/AgencySubmitView';
import { AgenciesService } from '../../../../share/services/agencies';
import {
  initialAgency,
  IAgency,
  IAgencyUIProps,
  IAgencyUIState,
  IUploadImageSettings
} from '../../../../share/types/agency';
import '../../../Routing/modules/agencies';
import './agency.css';
import SidebarContainer from 'src/modules/Routing/Sidebar/SidebarContainer';
import { RoutesModules } from 'src/modules/Routing/types';

export class AgencyComponent extends React.Component<IAgencyUIProps, IAgencyUIState> {
  public newAgency: IAgency;
  constructor(props: IAgencyUIProps) {
    super(props);
    this.state = {
      agencyInfo: _.cloneDeep(initialAgency),
      loading: true,
      submitted: false,
      beforeUpdate: null,
      loadingMessage: '',
      isEditAgency: false,
      checkingCode: false,
      invalidCode: false,
      logoSelected: true,
      imageUploadingStatus: {},
      agencySaved: false
    }
  }

  public isEditAgency = () => this.props.location.pathname.includes('edit');

  public componentDidMount() {
    if (this.isEditAgency()) {
      this.loadAgency();
    } else {
      this.setState({ agencyInfo: _.cloneDeep(initialAgency), loading: false });
    }
  }

  public componentDidUpdate(prevProps: IAgencyUIProps) {
    if (this.props.location.pathname !== prevProps.location.pathname) {
      if (this.isEditAgency()) {
        this.loadAgency();
      } else {
        this.setState({ agencyInfo: _.cloneDeep(initialAgency), loading: false });
      }
    }
  }

  public loadAgency = async () => {
    this.setState({ loading: true });
    const agencyCode = this.props.match.params.code;

    try {
      const res = await AgenciesService.getByID(agencyCode);
      if (!res) {
        throw { code: 1 }
      }
      if (_.isEmpty(res)) {
        this.setValue('agencyInfo.code', agencyCode);
        this.setState({ loading: false, isEditAgency: false });
        this.props.history.replace('/addAgency');
        toastr.info('This agency not exist in our records');
      } else {
        this.setState({
          agencyInfo: _.cloneDeep(res),
          beforeUpdate: _.cloneDeep(res),
          loading: false,
          isEditAgency: true
        });
      }
    }
    catch (error) {
      toastr.error('Error', 'There was a problem loading the agency data');
      this.setState({ loading: false });
    }
  }

  public agencyCodeValidator = (event: any) => {
    event.preventDefault();
    const agencyCode = event && event.target && event.target.value.toUpperCase();
    this.setValue('agencyInfo.code', agencyCode);
    this.setState({ checkingCode: true });
    this.checkCodeValidator(agencyCode);
  }

  public checkCodeValidator = _.debounce(async (agencyCode: string) => {
    if (agencyCode) {
      try {
        const res = await AgenciesService.exists(agencyCode);
        this.setValue('invalidCode', res);
        this.setState({ checkingCode: false });
      }
      catch (error) {
        toastr.error('Error', 'There was a problem checking the agency code');
        this.setState({ checkingCode: false });
      }
    } else {
      this.setState({ checkingCode: false, invalidCode: false });
    }
  }, 500);

  public setValue = (path: string, value: any) => {
    return new Promise<void>(resolve => {
      const state = { ...this.state };
      _.set(state, path, value)
      this.setState(state, () => resolve());
    })
  }

  public handleSubmit = () => {
    this.setValue('submitted', true);
  }

  public validateLogoDropZone = () => {
    this.setState({ logoSelected: this.state.agencyInfo.logo.url ? true : false });
    return this.state.agencyInfo.logo.url;
  }

  public handleValidSubmit = async () => {
    if (this.validateLogoDropZone()) {
      this.newAgency = _.cloneDeep(this.state.agencyInfo);
      try {
        await this.save();
      } catch {
        this.setState({ loading: false, loadingMessage: '' });
        toastr.error('Error', 'There was a problem connecting to the server');
      }
    } else {
      this.handleInvalidSubmit();
    }
  }

  public async save() {
    if (this.state.isEditAgency && _.isEqual(this.state.agencyInfo, this.state.beforeUpdate)) {
      return toastr.info('No changes reflected in this agency update');
    }
    if (!this.state.agencySaved) {
      this.setState({ loading: true, loadingMessage: 'Saving Agency' });
      if (this.state.isEditAgency) {
        await AgenciesService.update(this.state.beforeUpdate, { ...this.newAgency, logo: { url: this.state.beforeUpdate !== null ? this.state.beforeUpdate.logo.url : '' } });
      } else {
        await AgenciesService.save(this.newAgency);
      }
    }
    await this.uploadLogoImage();
    this.setState({ loading: true, loadingMessage: '' });
    if (this.wasErrorsUploadingLogo()) {
      toastr.error('There was a problem', 'Agency image logo failed to upload, try again')
    } else {
      this.savedCorrectly();
    }
  }

  public async uploadLogoImage() {
    this.setState({ loadingMessage: 'Uploading and Procesing Agency logo image', agencySaved: true })
    return Promise.all([this.uploadLogo()]);
  }

  public uploadLogo() {
    const { code, logo } = this.newAgency;
    const promise: any[] = [];

    if (logo.url && logo.url.length > 0) {
      promise.push(this.uploadImage({
        code, base64: logo.url, retry: 0
      }));
    }
    return Promise.all(promise);
  }

  public uploadImage(settings: IUploadImageSettings) {
    const { code, base64 } = settings;

    return this.ifWasNotUpload(() => {
      return Promise.resolve()
        .then(() => this.setImageState("UPLOADING"))
        .then(() => AgenciesService.uploadImage({ code, base64 }))
        .then((success) => this.manageResponse(success, settings))
    })
  }

  public manageResponse(success: boolean, settings: IUploadImageSettings) {
    if (success) {
      return this.setImageState("SUCCESS")
    } else {
      settings.retry++;
      return settings.retry > 2 ?
        this.setImageState("FAILED") :
        new Promise<void>(resolve => setTimeout(() => resolve(this.uploadImage(settings)), 500))
    }
  }

  public getErrorsUploadingImage() {
    const ius = this.state.imageUploadingStatus;

    return Object.keys(ius).filter((key) => ius[key].status === "FAILED")
  }

  public wasErrorsUploadingLogo = () => {
    return this.getErrorsUploadingImage().length > 0
  }

  public setImageState(imageState: 'UPLOADING' | 'SUCCESS' | 'FAILED') {
    return this.ifWasNotUpload(() => this.setValue(`imageUploadingStatus[logo]`, { status: imageState }))
  }

  public ifWasNotUpload(fn: () => Promise<any>) {
    return new Promise<void>((resolve) => this.wasImageUploaded() ? resolve() : resolve(fn()))
  }

  public wasImageUploaded() {
    const status = this.state.imageUploadingStatus;
    const image = this.state.agencyInfo.logo.url;
    const logo = 'logo';
    return status && status[logo] && status[logo].status === 'SUCCESS' || image.includes('https');
  }

  public handleInvalidSubmit = () => {
    this.validateLogoDropZone()
    toastr.error('Agency', 'Please fill all the required values');
  }

  public savedCorrectly() {
    toastr.success('Agency', 'The agency has been saved correctly');
    this.setState({ loading: false, loadingMessage: '' });
    ReactGA.event({
      category: 'Agency',
      action: `${this.isEditAgency() ? 'Updated' : 'Added new'} agency`,
      label: 'Agencies management'
    });
    this.props.history.replace('/agencies/view');
  }

  public render() {
    const {
      agencyInfo, isEditAgency, logoSelected, invalidCode,
      checkingCode, loading, imageUploadingStatus, loadingMessage
    } = this.state;

    return (
      <div className="add-agency">
        <div className="content white-background">
          <h1>{!loading ? isEditAgency ? 'Edit agency' : 'Add new agency' : ''}</h1>
          <Form
            onInvalidSubmit={this.handleInvalidSubmit}
            onValidSubmit={this.handleValidSubmit}
            onSubmit={this.handleSubmit}
          >
            <div className="margin-forms">
              <AgencyForm
                agencyCodeValidator={this.agencyCodeValidator}
                setValue={this.setValue}
                logoSelected={logoSelected}
                imageUploadingStatus={imageUploadingStatus}
                agencyInfo={agencyInfo}
                loading={loading}
                invalidCode={invalidCode}
                checkingCode={checkingCode}
                isEditAgency={isEditAgency}
              />
              <AgencySubmitView
                wasErrorsUploadingLogo={this.wasErrorsUploadingLogo}
                loading={loading}
                checkingCode={checkingCode}
                loadingMessage={loadingMessage}
                invalidCode={invalidCode}
              />
            </div>
          </Form>
        </div>
      </div>
    );
  }
}

export default SidebarContainer(RoutesModules.AGENCIES)(AgencyComponent);
